import {ThemeProvider, Box, Grid} from '@primer/react-brand'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import {ContentfulInlineFootnotesList} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnotesList'
import {FootnotesProvider} from '@github-ui/swp-core/components/contentful/FootnotesContext'
import {useContentfulData} from '@github-ui/swp-core/lib/hooks/use-contentful-data'

import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'
import type {GenericSectionWithIds} from '../../../../brand/lib/types/contentful'
import {VendorPixelTracker} from '../../../../components/VendorPixelTracker'

import {HeroSection} from './HeroSection'
import {PillarsSection} from './PillarsSection'
import {FeaturesSection} from './FeaturesSection'
import {RiverAccordionSection} from './RiverAccordionSection'
import {IdeSection} from './IdeSection'
import {ResourcesSection} from './ResourcesSection'
import {CtaBannerSection} from './CtaBannerSection'
import {FaqSection} from './FaqSection'

import {PlanTypeContextProvider} from '../_context/PlanTypeContext'

export default function FeaturesCopilotAiCodeEditorIndex() {
  const {contentfulRawJsonResponse, vendorPixels} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = useContentfulData(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)

  const {template} = page.fields
  const {subnav} = template.fields
  const {
    featuresCopilotAiCodeEditorHeroSection,
    featuresCopilotAiCodeEditorPillarsSection,
    featuresCopilotAiCodeEditorFeaturesSection,
    featuresCopilotAiCodeEditorRiverAccordionSection,
    featuresCopilotAiCodeEditorIdeSection,
    featuresCopilotAiCodeEditorResourcesSection,
    featuresCopilotAiCodeEditorCtaBannerSection,
    featuresCopilotAiCodeEditorFaqSection,
  } = page.ids as {
    featuresCopilotAiCodeEditorHeroSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorPillarsSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorFeaturesSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorRiverAccordionSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorIdeSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorResourcesSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorCtaBannerSection: GenericSectionWithIds
    featuresCopilotAiCodeEditorFaqSection: GenericSectionWithIds
  }

  return (
    <VendorPixelTracker vendorPixels={vendorPixels ?? []}>
      <FootnotesProvider>
        <PlanTypeContextProvider>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <ThemeProvider colorMode="dark" className="lp-Copilot position-relative">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <Box className="lp-SubNav-spacer" />

            {subnav ? (
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" />
            ) : null}

            {featuresCopilotAiCodeEditorHeroSection ? (
              <HeroSection contentfulContent={featuresCopilotAiCodeEditorHeroSection} />
            ) : null}

            {featuresCopilotAiCodeEditorIdeSection ? (
              <IdeSection contentfulContent={featuresCopilotAiCodeEditorIdeSection} />
            ) : null}

            {featuresCopilotAiCodeEditorPillarsSection ? (
              <PillarsSection contentfulContent={featuresCopilotAiCodeEditorPillarsSection} />
            ) : null}

            {featuresCopilotAiCodeEditorFeaturesSection ? (
              <FeaturesSection contentfulContent={featuresCopilotAiCodeEditorFeaturesSection} />
            ) : null}

            {featuresCopilotAiCodeEditorRiverAccordionSection ? (
              <RiverAccordionSection contentfulContent={featuresCopilotAiCodeEditorRiverAccordionSection} />
            ) : null}

            {featuresCopilotAiCodeEditorCtaBannerSection ? (
              <CtaBannerSection contentfulContent={featuresCopilotAiCodeEditorCtaBannerSection} />
            ) : null}

            {featuresCopilotAiCodeEditorResourcesSection ? (
              <ResourcesSection contentfulContent={featuresCopilotAiCodeEditorResourcesSection} />
            ) : null}

            {featuresCopilotAiCodeEditorFaqSection ? (
              <FaqSection contentfulContent={featuresCopilotAiCodeEditorFaqSection} />
            ) : null}

            <Grid>
              <Grid.Column span={12}>
                <ContentfulInlineFootnotesList />
              </Grid.Column>
            </Grid>
          </ThemeProvider>
        </PlanTypeContextProvider>
      </FootnotesProvider>
    </VendorPixelTracker>
  )
}
